import React, { useMemo, useState } from 'react';
import { APITypes, MedicalDataTypes, Dynamo } from '../../../../primary-record-types';
import { getIntl } from '../../../../utils/intl';
import { FastField } from 'formik';
import { getDateFormatValidator, required } from '../../../../utils/validators';
import ImagesField, { formValueImagesToResourceImageUpdates, resourceImagesToFormValueImages } from '../ImagesField';
import { FormValueImage } from '../ImagesField/types';
import { medicationFrequency } from '../../../../utils/sharedMessages';
import { handleFormikSubmit } from '../../../../utils/handleFormikSubmit';
import ProviderFields, { getProviderPropsInitialValues, getProviderSubmissionValues } from '../ProviderFields';
import useRecordEvent from '../../../../hooks/useRecordEvent';
import { Form } from '../../../v2BaseComponents/Form/Form';
import { FormikInputs } from '../../../v2BaseComponents/Inputs';
import Stack, { FormStack } from '../../../v2BaseComponents/Stack';
import { CenteredFormRow, SplitFormRow } from '../../../v2BaseComponents/Form';
import { FDASearchFormValue } from './FDASearchForm';
import Text from '../../../v2BaseComponents/Text';
import Divider from '../../../v2BaseComponents/Divider';
import { View, Modal, TouchableWithoutFeedback, StyleSheet } from 'react-native';
import Button from '../../../v2BaseComponents/Button';

const intl = getIntl('medicationsForm', {
    save: 'Save',
    medication: 'Medication',
    notes: 'Additional Information',
    startDate: 'Start date',
    endDate: 'Stop date',
    status: 'Status',
    genericName: 'Generic Name or Active Ingredient',
    strength: 'Strength',
    form: 'Form',
    route: 'Route',
    quantity: 'Quantity',
    frequency: 'Frequency',
    asNeeded: 'This medication is taken as needed',
    whenToTake: 'When to Take',
    isActive: 'This medication is actively being taken',
    numberOfRefills: 'Number of Refills',
    lastRefill: 'Last Refill',
    selectFrequency: 'Select Frequency',
    medicationHeader: 'Medication',
    prescriptionHeader: 'Prescription Details',
});

export type MedicationFormOwnProps = {
    isLoading: boolean;
    startDateTime?: string;
    defaultResource?: MedicalDataTypes.PrimaryRecordMedication;
    defaultImages?: Dynamo.ResourceImage[];
    onSubmit: (data: APITypes.medicalDataService.UpdateResourceBody<MedicalDataTypes.PrimaryRecordMedication>) => void;
    /**
     * Lets the screen interject before a save. It resolves with the body to submit - optionally
     * adding fields such as group ids - or null if the user backed out, in which case the form
     * stays dirty so the unsaved-changes guard still applies.
     */
    confirmBeforeSubmit?: (
        data: APITypes.medicalDataService.UpdateResourceBody<MedicalDataTypes.PrimaryRecordMedication>,
    ) => Promise<APITypes.medicalDataService.UpdateResourceBody<MedicalDataTypes.PrimaryRecordMedication> | null>;
    isDisabled: boolean;
    profileId: string;
    resourceId?: string;
    providers: APITypes.medicalProfileService.ProviderCareTeamMemberDetails<APITypes.medicalProfileService.ProviderCareTeamContactDetails>[];
    onCancel: () => void;
    fdaSearchValue?: FDASearchFormValue;
};

type MedicationResourceFormValue = MedicalDataTypes.PrimaryRecordMedication & {
    selectExistingProvider: boolean;
};

type FormValue = {
    resource: MedicationResourceFormValue;
    images: FormValueImage[];
};

type GetDefaultFormValueArgs = {
    selectExistingProvider: boolean;
    startDateTime?: string;
    endDateTime?: string;
    fdaSearchValue: FDASearchFormValue | undefined;
};

const maybeFlattenStringArray = (value: string[] | string | undefined): string | undefined => {
    if (Array.isArray(value)) {
        return value.join(', ');
    }
    return value;
};

/**
 * Checks if an end date is in the past.
 * Date-only strings (YYYY-MM-DD) are normalized to end-of-day for comparison.
 */
export const isEndDateInPast = (endDateTime: string | undefined | null): boolean => {
    if (!endDateTime) {
        return false;
    }

    const endDate = new Date(endDateTime);
    const isDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(endDateTime);
    if (isDateOnly) {
        endDate.setUTCHours(23, 59, 59, 999);
    }

    const now = new Date();
    return endDate.getTime() <= now.getTime();
};

const getDefaultFormValue = ({
    selectExistingProvider,
    startDateTime,
    fdaSearchValue,
}: GetDefaultFormValueArgs): FormValue => ({
    resource: {
        id: '', //ignored
        version: '1',
        type: 'medication',
        name: fdaSearchValue?.nameAndActiveIngredient?.name || '',
        genericNameOrActiveIngredient:
            maybeFlattenStringArray(fdaSearchValue?.nameAndActiveIngredient?.genericNameOrActiveIngredient) || '',
        startDateTime: startDateTime || '',
        endDateTime: '',
        fdaName: fdaSearchValue?.nameAndActiveIngredient?.name || null,
        strength: fdaSearchValue?.strength || null,
        form: fdaSearchValue?.form || null,
        route: fdaSearchValue?.route || null,
        quantity: null,
        frequency: null,
        asNeeded: null,
        isActive: true,
        whenToTake: null,
        numberOfRefills: null,
        lastRefillDateTime: null,
        notes: '',
        selectExistingProvider,
        providerCareTeamMemberId: null,
        providerName: null,
    },
    images: [],
});

export type MedicationFormProps = MedicationFormOwnProps;

const MedicationForm: React.FC<MedicationFormProps> = ({
    isLoading,
    onSubmit,
    confirmBeforeSubmit,
    isDisabled,
    defaultImages,
    defaultResource,
    startDateTime,
    profileId,
    resourceId,
    providers,
    onCancel,
    fdaSearchValue,
}) => {
    const [showPastDateConfirmation, setShowPastDateConfirmation] = useState(false);
    const [pendingSubmission, setPendingSubmission] = useState<{ values: FormValue; helpers: any } | null>(null);

    const renderTextDivider = (text: string) => (
        <Stack vertical fullWidth space={2}>
            <Text variant="bold-h4">{text}</Text>
            <Divider horizontal />
        </Stack>
    );

    const initialValues = useMemo<FormValue>(() => {
        return defaultResource && defaultImages
            ? {
                  ...(getProviderPropsInitialValues(
                      providers,
                      {
                          resource: {
                              ...defaultResource,
                              selectExistingProvider: !!defaultResource.providerCareTeamMemberId,
                          },
                      },
                      getDefaultFormValue({
                          selectExistingProvider: !!defaultResource.providerCareTeamMemberId,
                          startDateTime,
                          fdaSearchValue,
                      }),
                  ) as Pick<FormValue, 'resource'>),
                  images: resourceImagesToFormValueImages(defaultImages),
              }
            : getDefaultFormValue({
                  selectExistingProvider: providers.length > 0,
                  startDateTime,
                  fdaSearchValue,
              });
    }, [defaultImages, defaultResource, providers, startDateTime, fdaSearchValue]);
    const recordEvent = useRecordEvent();

    const handleSubmit = async (values: FormValue, helpers: any) => {
        const data = {
            resourceUpdate: {
                ...getProviderSubmissionValues(values.resource, providers, true),
                startDateTime: values.resource.startDateTime || undefined,
                endDateTime: values.resource.endDateTime || '',
            },
            imageUpdates: formValueImagesToResourceImageUpdates(values.images),
        };

        if (confirmBeforeSubmit) {
            // The unsaved-changes guard is suppressed while Formik reports the form as
            // submitting, so release that for as long as the confirmation is open. Otherwise
            // navigating away from an open confirmation discards the edits without warning.
            helpers.setSubmitting(false);
            const confirmed = await confirmBeforeSubmit(data);
            // Nothing is saved and the baseline is not reset unless the screen confirms, so
            // backing out leaves the form dirty with the exit guard still armed.
            if (!confirmed) return;
            helpers.setSubmitting(true);
            onSubmit(confirmed);
        } else {
            onSubmit(data);
        }
        recordEvent(!!defaultResource ? 'editMedication' : 'createMedication');
        handleFormikSubmit(values, helpers);
    };

    const handleConfirmPastDate = () => {
        if (!pendingSubmission) return;
        setShowPastDateConfirmation(false);
        const { values, helpers } = pendingSubmission;
        setPendingSubmission(null);
        helpers.setSubmitting(true);
        handleSubmit(values, helpers);
    };

    const handleCancelPastDate = () => {
        setShowPastDateConfirmation(false);
        if (pendingSubmission) {
            pendingSubmission.helpers.setSubmitting(false);
            setPendingSubmission(null);
        }
    };

    return (
        <>
            <Form
                actionContainerSubmit
                initialValues={initialValues}
                onSubmit={(values, helpers) => {
                    if (isEndDateInPast(values.resource.endDateTime) && values.resource.isActive) {
                        helpers.setSubmitting(false);
                        setPendingSubmission({ values, helpers });
                        setShowPastDateConfirmation(true);
                        return;
                    }
                    handleSubmit(values, helpers);
                }}
                onCancel={onCancel}
                cancelButtonVariant="secondary"
            >
                {({ values, setFieldValue }) => {
                    return (
                        <FormStack>
                            {renderTextDivider(intl('medicationHeader'))}
                            <CenteredFormRow>
                                <FastField
                                    name="resource.isActive"
                                    component={FormikInputs.Checkbox}
                                    title={intl('isActive')}
                                    isDisabled={isDisabled || isLoading}
                                />
                            </CenteredFormRow>
                            <FastField
                                component={FormikInputs.Input}
                                name="resource.name"
                                isRequired
                                title={intl('medication')}
                                isDisabled={isDisabled || isLoading}
                                validate={required}
                                forceFullWidth
                            />
                            <SplitFormRow>
                                <FastField
                                    name="resource.genericNameOrActiveIngredient"
                                    title={intl('genericName')}
                                    component={FormikInputs.Input}
                                    isDisabled={isDisabled || isLoading}
                                />
                                <FastField
                                    name="resource.strength"
                                    title={intl('strength')}
                                    component={FormikInputs.Input}
                                    isDisabled={isDisabled || isLoading}
                                />
                            </SplitFormRow>
                            <SplitFormRow>
                                <FastField
                                    name="resource.form"
                                    title={intl('form')}
                                    component={FormikInputs.Input}
                                    isDisabled={isDisabled || isLoading}
                                />
                                <FastField
                                    name="resource.route"
                                    title={intl('route')}
                                    component={FormikInputs.Input}
                                    isDisabled={isDisabled || isLoading}
                                />
                            </SplitFormRow>
                            {renderTextDivider(intl('prescriptionHeader'))}
                            <ProviderFields
                                value={values}
                                providers={providers}
                                isDisabled={isDisabled}
                                isLoading={isLoading}
                                setFieldValue={setFieldValue}
                            />
                            <SplitFormRow>
                                <FastField
                                    name="resource.quantity"
                                    title={intl('quantity')}
                                    component={FormikInputs.Input}
                                    isDisabled={isDisabled || isLoading}
                                />
                                <FastField
                                    name="resource.frequency"
                                    title={intl('frequency')}
                                    component={FormikInputs.Select}
                                    selectOptions={(
                                        [
                                            'daily',
                                            '2-daily',
                                            '3-daily',
                                            'weekly',
                                            'monthly',
                                            'continuously',
                                            'other',
                                        ] as const
                                    ).map((_) => ({
                                        label: medicationFrequency(_),
                                        value: _,
                                    }))}
                                    isDisabled={isDisabled || isLoading}
                                    selectFrequency={intl('selectFrequency')}
                                    allowEmpty
                                />
                            </SplitFormRow>
                            <SplitFormRow styleRight={{ alignSelf: 'stretch', justifyContent: 'flex-start' }}>
                                <FastField
                                    name="resource.whenToTake"
                                    title={intl('whenToTake')}
                                    component={FormikInputs.Input}
                                    isDisabled={isDisabled || isLoading}
                                />
                                <View style={{ alignSelf: 'stretch', justifyContent: 'center' }}>
                                    <FastField
                                        name="resource.asNeeded"
                                        component={FormikInputs.Checkbox}
                                        title={intl('asNeeded')}
                                        isDisabled={isDisabled || isLoading}
                                    />
                                </View>
                            </SplitFormRow>
                            <SplitFormRow>
                                <FastField
                                    name="resource.startDateTime"
                                    title={intl('startDate')}
                                    component={FormikInputs.DateTimePicker}
                                    validate={getDateFormatValidator('MM/DD/YYYY')}
                                    isDisabled={isDisabled || isLoading}
                                />
                                <FastField
                                    name="resource.endDateTime"
                                    title={intl('endDate')}
                                    component={FormikInputs.DateTimePicker}
                                    validate={getDateFormatValidator('MM/DD/YYYY')}
                                    isDisabled={isDisabled || isLoading}
                                />
                            </SplitFormRow>
                            <SplitFormRow>
                                <FastField
                                    name="resource.numberOfRefills"
                                    title={intl('numberOfRefills')}
                                    component={FormikInputs.Input}
                                    isDisabled={isDisabled || isLoading}
                                />
                                <FastField
                                    name="resource.lastRefillDateTime"
                                    title={intl('lastRefill')}
                                    component={FormikInputs.DateTimePicker}
                                    validate={getDateFormatValidator('MM/DD/YYYY')}
                                    isDisabled={isDisabled || isLoading}
                                />
                            </SplitFormRow>
                            <FastField
                                component={FormikInputs.TextArea}
                                name="resource.notes"
                                title={intl('notes')}
                                isDisabled={isDisabled || isLoading}
                                forceFullWidth
                            />
                            <CenteredFormRow>
                                <ImagesField
                                    isDisabled={isDisabled || isLoading}
                                    values={values.images}
                                    resourceId={resourceId}
                                    profileId={profileId}
                                    category="medication"
                                    setFieldValue={setFieldValue}
                                />
                            </CenteredFormRow>
                        </FormStack>
                    );
                }}
            </Form>
            <Modal
                visible={showPastDateConfirmation}
                transparent
                animationType="fade"
                onRequestClose={handleCancelPastDate}
            >
                <TouchableWithoutFeedback onPress={handleCancelPastDate}>
                    <View style={styles.modalOverlay}>
                        <TouchableWithoutFeedback>
                            <View style={styles.modalContent}>
                                <Text variant="h4" style={styles.modalTitle}>
                                    This date is in the past
                                </Text>
                                <Text variant="body-1" style={styles.modalMessage}>
                                    Your medication will be saved as inactive. Do you want to continue?
                                </Text>
                                <Stack horizontal fullWidth space={2} style={styles.modalActions}>
                                    <Button
                                        title="Cancel"
                                        variant="secondary"
                                        onPress={handleCancelPastDate}
                                        style={styles.modalButton}
                                    />
                                    <Button
                                        title="Continue"
                                        variant="primary"
                                        onPress={handleConfirmPastDate}
                                        style={styles.modalButton}
                                    />
                                </Stack>
                            </View>
                        </TouchableWithoutFeedback>
                    </View>
                </TouchableWithoutFeedback>
            </Modal>
        </>
    );
};

const styles = StyleSheet.create({
    modalOverlay: {
        flex: 1,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        justifyContent: 'center',
        alignItems: 'center',
        paddingHorizontal: 20,
    },
    modalContent: {
        backgroundColor: 'white',
        borderRadius: 12,
        padding: 24,
        width: '90%',
        maxWidth: 400,
        shadowColor: '#000',
        shadowOffset: {
            width: 0,
            height: 2,
        },
        shadowOpacity: 0.25,
        shadowRadius: 3.84,
        elevation: 5,
    },
    modalTitle: {
        marginBottom: 12,
        textAlign: 'center',
    },
    modalMessage: {
        marginBottom: 24,
        textAlign: 'center',
    },
    modalActions: {
        justifyContent: 'center',
    },
    modalButton: {
        flex: 1,
    },
});

export default React.memo(MedicationForm);
